import { Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getButtonClasses, BUTTON_ICON_GLYPH_CLASS } from "@/components/ui/atoms/Button";
import { ManatalIntegrationCard } from "../components/integrations/ManatalIntegrationCard";
import { McpIntegrationCard } from "../components/integrations/McpIntegrationCard";
import { ComingSoonIntegrationCard } from "../components/integrations/ComingSoonIntegrationCard";
import { MCP_INTEGRATION_ENABLED } from "@/constants/featureFlags";

const ROADMAP: Array<{
  name: string;
  description: string;
  monogram: string;
  category: string;
  logoUrl?: string;
}> = [
  {
    name: 'Greenhouse',
    description: 'Two-way sync with Greenhouse, import candidates and write interview results back to the candidate timeline.',
    monogram: 'GH',
    category: 'ATS',
    logoUrl: '/integration-logos/greenhouse.png',
  },
  {
    name: 'Ashby',
    description: 'Pipeline-aware sync with Ashby. Trigger interviews from a stage move, write scores back automatically.',
    monogram: 'AS',
    category: 'ATS',
    logoUrl: '/integration-logos/ashby.png',
  },
  {
    name: 'Workday',
    description: 'Connect Workday Recruiting to source candidates and persist assessments inside the Workday job record.',
    monogram: 'WD',
    category: 'ATS · HRIS',
    logoUrl: '/integration-logos/workday.png',
  },
  {
    name: 'Indeed',
    description: 'Auto-import applicants from Indeed job postings, score CVs on arrival, and trigger AI interviews.',
    monogram: 'IN',
    category: 'Job board',
    logoUrl: '/integration-logos/indeed.png',
  },
  {
    name: 'LinkedIn Talent',
    description: 'Sync InMails and applicants from LinkedIn Talent Hub directly into your Invirtus pipeline.',
    monogram: 'LI',
    category: 'Sourcing',
    logoUrl: '/integration-logos/linkedin.png',
  },
  {
    name: 'Slack',
    description: 'Notify channels when candidates complete interviews, when assessments arrive, or limits are nearing.',
    monogram: 'SL',
    category: 'Notifications',
    logoUrl: '/integration-logos/slack.png',
  },
];

export const IntegrationsPage = () => {
  const { t } = useTranslation('common');
  return (
    <div className="bg-background-gray h-full overflow-y-auto">
      <div className="px-6 lg:px-10 py-6 md:py-10">
        {/* Page header */}
        <div className="mb-8 max-w-3xl">
          <h4 className="text-xl md:text-2xl font-semibold text-dark-invirtus font-heading leading-tight">
            {t('integrations.title')}
          </h4>
          <p className="mt-1 text-sm text-dark-invirtus/60 font-body">
            {t('integrations.description')}
          </p>
        </div>

        {/* Available — Manatal as a single horizontal row tile. */}
        <section className="mb-10">
          <h5 className="text-[11px] font-semibold uppercase tracking-wider text-dark-invirtus/50 font-body mb-3">
            {t('integrations.available')}
          </h5>
          <div className="space-y-4">
            <ManatalIntegrationCard />
            {MCP_INTEGRATION_ENABLED && <McpIntegrationCard />}
          </div>
        </section>

        {/* On the roadmap — uniform grid, all coming-soon cards same shape */}
        <section>
          <h5 className="text-[11px] font-semibold uppercase tracking-wider text-dark-invirtus/50 font-body mb-3">
            {t('integrations.on_the_roadmap')}
          </h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {ROADMAP.map((item) => (
              <ComingSoonIntegrationCard
                key={item.name}
                name={item.name}
                description={item.description}
                monogram={item.monogram}
                category={item.category}
                logoUrl={item.logoUrl}
              />
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <p className="text-xs text-dark-invirtus/50 font-body">
              {t('integrations.have_a_request')}
            </p>
            <a
              href="mailto:support@invirtus.co?subject=Integration%20request"
              className={getButtonClasses(
                'contained',
                'light',
                'xs',
                false,
                'no-underline gap-1.5',
              )}
            >
              <Mail className={BUTTON_ICON_GLYPH_CLASS.xs} aria-hidden />
              support@invirtus.co
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};
